/**
 * As telas que o link do e-mail abre: confirmar e-mail, redefinir senha e
 * aceitar convite.
 *
 * O token vem na query string e carrega o tenant ("<loja_id>.<segredo>"), o
 * que permite ao backend verificar dentro do RLS como qualquer outra operação
 * — sem caminho privilegiado.
 */
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Faixa } from '../../componentes/Basicos'
import { Botao } from '../../componentes/Botao'
import { Campo, CampoSenha } from '../../componentes/Campos'
import { Lockup } from '../../componentes/Marca'
import { api, ErroApi } from '../../plataforma/api'

/** Moldura centralizada comum às três telas. */
function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-superficie px-6 py-16">
      <div className="w-full max-w-[420px]">
        <div className="mb-10 flex justify-center">
          <Lockup />
        </div>
        {children}
      </div>
    </div>
  )
}

export function TelaConfirmarEmail() {
  const [parametros] = useSearchParams()
  const token = parametros.get('token') ?? ''

  const [situacao, setSituacao] = useState<'confirmando' | 'ok' | 'falhou'>('confirmando')
  const [mensagem, setMensagem] = useState('')

  useEffect(() => {
    if (token === '') {
      setSituacao('falhou')
      setMensagem('Link de confirmação inválido ou expirado.')
      return
    }

    let ativo = true
    api
      .post<{ mensagem: string }>('/email/confirmacoes', { token })
      .then((resposta) => {
        if (!ativo) return
        setSituacao('ok')
        setMensagem(resposta.mensagem)
      })
      .catch((erro: unknown) => {
        if (!ativo) return
        setSituacao('falhou')
        setMensagem(mensagemDe(erro))
      })

    return () => {
      ativo = false
    }
  }, [token])

  return (
    <Moldura>
      <div className="text-center">
        {situacao === 'confirmando' && (
          <p className="text-sm text-texto-suave">Confirmando seu e-mail…</p>
        )}

        {situacao !== 'confirmando' && (
          <>
            <div
              aria-hidden
              className={`mx-auto mb-[22px] flex h-[58px] w-[58px] items-center justify-center rounded-full text-2xl font-bold ${
                situacao === 'ok' ? 'bg-ok-suave text-ok-texto' : 'bg-erro-suave text-erro-texto'
              }`}
            >
              {situacao === 'ok' ? '✓' : '!'}
            </div>
            <h1 className="mb-[10px] font-titulo text-[26px] font-bold text-titulo">
              {situacao === 'ok' ? 'E-mail confirmado' : 'Não foi possível confirmar'}
            </h1>
            <p className="mb-6 text-sm leading-[1.65] text-texto-suave">{mensagem}</p>
            <Link to="/entrar">
              <Botao porte="auth" larguraTotal>Ir para o login</Botao>
            </Link>
          </>
        )}
      </div>
    </Moldura>
  )
}

export function TelaRedefinirSenha() {
  const [parametros] = useSearchParams()
  const navegar = useNavigate()
  const token = parametros.get('token') ?? ''

  const [nova, setNova] = useState('')
  const [repetir, setRepetir] = useState('')
  const [erro, setErro] = useState('')
  const [pronto, setPronto] = useState(false)

  async function salvar(evento: FormEvent) {
    evento.preventDefault()

    if (nova.length < 8) {
      setErro('A senha precisa de 8 caracteres ou mais.')
      return
    }
    if (nova !== repetir) {
      setErro('As senhas não coincidem.')
      return
    }

    try {
      await api.post('/senha/redefinicoes', {
        token,
        nova_senha: nova,
        repetir_senha: repetir,
      })
      setPronto(true)
      setErro('')
      setTimeout(() => navegar('/entrar'), 1200)
    } catch (problema) {
      setErro(mensagemDe(problema))
    }
  }

  return (
    <Moldura>
      <h1 className="mb-2 font-titulo text-[28px] font-bold text-titulo">Definir nova senha</h1>
      <p className="mb-[26px] text-sm leading-[1.65] text-texto-suave">
        Use 8 caracteres ou mais. Evite a mesma senha de outros sistemas.
      </p>

      <form onSubmit={salvar} noValidate>
        <CampoSenha
          variante="auth"
          rotulo="Nova senha"
          autoComplete="new-password"
          placeholder="••••••••"
          comMedidor
          value={nova}
          onChange={(e) => {
            setNova(e.target.value)
            setErro('')
          }}
        />

        <div className="mb-5">
          <CampoSenha
            variante="auth"
            rotulo="Repetir senha"
            autoComplete="new-password"
            placeholder="••••••••"
            value={repetir}
            onChange={(e) => {
              setRepetir(e.target.value)
              setErro('')
            }}
          />
        </div>

        <Botao type="submit" porte="auth" larguraTotal disabled={pronto}>
          {pronto ? 'Senha alterada' : 'Salvar nova senha'}
        </Botao>
      </form>

      {pronto && <Faixa tipo="sucesso">Senha alterada. Você já pode entrar.</Faixa>}
      {erro && <Faixa tipo="erro">{erro}</Faixa>}

      <div className="mt-4 text-center">
        <Link to="/entrar" className="text-[13.5px] font-semibold text-texto-suave">
          Voltar para o login
        </Link>
      </div>
    </Moldura>
  )
}

export function TelaAceitarConvite() {
  const [parametros] = useSearchParams()
  const navegar = useNavigate()
  const token = parametros.get('token') ?? ''

  const [nome, setNome] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState('')
  const [pronto, setPronto] = useState(false)

  async function aceitar(evento: FormEvent) {
    evento.preventDefault()

    if (!nome.trim()) {
      setErro('Informe seu nome.')
      return
    }
    if (senha.length < 8) {
      setErro('A senha precisa de 8 caracteres ou mais.')
      return
    }

    try {
      await api.post('/convites/aceites', { token, nome: nome.trim(), senha })
      setPronto(true)
      setErro('')
      setTimeout(() => navegar('/entrar'), 1200)
    } catch (problema) {
      setErro(mensagemDe(problema))
    }
  }

  return (
    <Moldura>
      <h1 className="mb-2 font-titulo text-[28px] font-bold text-titulo">Criar seu acesso</h1>
      <p className="mb-[26px] text-sm leading-[1.65] text-texto-suave">
        Você foi convidado para operar no Lumma Store. Defina seu nome e uma senha.
      </p>

      <form onSubmit={aceitar} noValidate>
        <div className="mb-[18px]">
          <Campo
            variante="auth"
            rotulo="Seu nome"
            autoComplete="name"
            placeholder="Bruno Souza"
            value={nome}
            onChange={(e) => {
              setNome(e.target.value)
              setErro('')
            }}
          />
        </div>

        <div className="mb-5">
          <CampoSenha
            variante="auth"
            rotulo="Senha"
            autoComplete="new-password"
            placeholder="••••••••"
            comMedidor
            value={senha}
            onChange={(e) => {
              setSenha(e.target.value)
              setErro('')
            }}
          />
        </div>

        <Botao type="submit" porte="auth" larguraTotal disabled={pronto}>
          {pronto ? 'Acesso criado' : 'Criar acesso'}
        </Botao>
      </form>

      {pronto && <Faixa tipo="sucesso">Acesso criado. Você já pode entrar.</Faixa>}
      {erro && <Faixa tipo="erro">{erro}</Faixa>}
    </Moldura>
  )
}

function mensagemDe(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message
  return 'Não foi possível concluir a operação.'
}
